import { useState } from "react";
import { Music2, Pause, Play, Search, Volume2, VolumeX, Loader2, Youtube, X } from "lucide-react";
import { useMusic } from "@/components/MusicProvider";

export const MusicPlayer = () => {
  const m = useMusic();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  if (!m) return null;
  const { playing, blocked, current, results, searching, volume, muted, search, playVideo, togglePlay, changeVolume, toggleMute } = m;
  const active = playing && !muted;

  return (
    <div className="relative shrink-0" data-testid="music-player" data-audio-state={playing ? "playing" : "paused"}>
      <button
        data-testid="music-controls-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        title="Music — play any YouTube song or video on the city screen"
        className={`relative w-9 h-9 rounded-lg grid place-items-center transition-colors ${active ? "bg-emerald-500/10 text-emerald-300" : blocked ? "text-cyan-300" : "text-zinc-500 hover:bg-white/5 hover:text-cyan-300"}`}
      >
        <Music2 size={17} />
        {blocked && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping" />}
      </button>

      {open && (
        <div data-testid="music-controls" className="absolute top-12 right-0 w-80 bg-[#0a1420] border border-cyan-400/25 rounded-xl shadow-2xl p-3.5 z-50">
          <div className="flex items-center gap-2 mb-2.5">
            <Youtube size={15} className="text-red-400" />
            <span className="text-sm font-tactical font-semibold text-zinc-100">City Music</span>
            <span className="ml-auto font-mono text-[9px] text-zinc-500 uppercase tracking-widest">plays on the city screen</span>
            <button data-testid="music-controls-close" onClick={() => setOpen(false)} title="Close" className="w-6 h-6 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-white/10 transition-colors"><X size={14} /></button>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); search(q); }} className="flex items-center gap-1.5 mb-2.5">
            <div className="flex-1 relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="music-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a song, artist, or paste a link"
                className="w-full bg-[#05090f] border border-white/10 rounded-lg pl-8 pr-2.5 py-2 text-xs text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              />
            </div>
            <button data-testid="music-search-button" type="submit" disabled={searching} className="h-8 px-3 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-[#04070c] text-xs font-bold grid place-items-center">
              {searching ? <Loader2 size={13} className="animate-spin" /> : "Go"}
            </button>
          </form>

          {results.length > 0 && (
            <div data-testid="music-results" className="max-h-52 overflow-y-auto thin-scroll space-y-1 mb-2.5 -mr-1 pr-1">
              {results.map((v) => (
                <button
                  key={v.id}
                  data-testid={`music-result-${v.id}`}
                  onClick={() => { playVideo(v); }}
                  className={`w-full flex items-center gap-2 p-1.5 rounded-lg text-left transition-colors ${current?.id === v.id ? "bg-cyan-500/15" : "hover:bg-white/5"}`}
                >
                  <img src={v.thumbnail} alt="" className="w-14 h-9 rounded object-cover shrink-0 bg-black/40" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] text-zinc-100 leading-tight line-clamp-2">{v.title}</span>
                    <span className="block text-[9px] text-zinc-500 truncate">{v.channel}{v.duration ? ` · ${v.duration}` : ""}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {current && (
            <div data-testid="music-now-playing" className="flex items-center gap-2 rounded-lg bg-white/5 p-2 mb-2.5">
              <img src={current.thumbnail} alt="" className="w-10 h-7 rounded object-cover shrink-0 bg-black/40" />
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] text-cyan-300 font-mono uppercase tracking-wider leading-none mb-0.5">{playing ? "Now playing" : blocked ? "Tap play to resume" : "Paused"}</span>
                <span className="block text-[11px] text-zinc-200 truncate">{current.title}</span>
              </span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button data-testid="music-play-pause" aria-label={playing ? "Pause" : "Play"} disabled={!current} onClick={togglePlay} className="w-10 h-10 rounded-full bg-cyan-400 text-black grid place-items-center hover:bg-cyan-300 disabled:opacity-40">
              {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
            </button>
            <button data-testid="music-mute-toggle" aria-label={muted ? "Unmute" : "Mute"} onClick={toggleMute} className="text-zinc-400 p-1.5 hover:text-cyan-300">
              {muted || !volume ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <input data-testid="music-volume" aria-label="Volume" type="range" min="0" max="100" step="1" value={volume} onChange={(e) => changeVolume(Number(e.target.value))} className="w-full accent-cyan-400" />
          </div>

          <p data-testid="music-status" className="text-[10px] text-zinc-500 mt-2.5">
            {current ? "Your pick shows on the big screen in the city — and keeps playing everywhere." : "Search anything on YouTube and cast it to the city's giant screen."}
          </p>
        </div>
      )}
    </div>
  );
};
